import Column from '@components/Column';
import Radio from '@components/Radio';
import Switch from '@components/Switch';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { CashReceiptIssueType } from '@models/order';
import { useTranslation } from 'react-i18next';

import SectionTitle from '../common/SectionTitle';
import useCashReceipt from './hooks/useCashReceipt';

const ISSUE_TYPES: CashReceiptIssueType[] = ['PERSONAL', 'BUSINESS'];

/** 현금영수증. 신청을 고르면 발급 유형과 번호를 화면에서 바로 입력한다. */
function CashReceipt() {
    const { t } = useTranslation('order');
    const {
        isAvailable, isApplied, issueType, isBusiness, formattedNumber, error, isSave,
        changeApplied, changeIssueType, changeIssueNumber, changeSave,
    } = useCashReceipt();

    if (!isAvailable) {
        return null;
    }

    return (
        <Container fullWidth spacing={2}>
            <Header>
                <SectionTitle>{t('cashReceipt.title')}</SectionTitle>
                <Segment>
                    {([
                        [true, t('cashReceipt.apply')],
                        [false, t('cashReceipt.notApplied')],
                    ] as [boolean, string][]).map(([applied, label]) => (
                        <SegmentButton key={label}
                            type='button'
                            aria-pressed={isApplied === applied}
                            onClick={() => changeApplied(applied)}>
                            {label}
                        </SegmentButton>
                    ))}
                </Segment>
            </Header>
            {isApplied && (
                <>
                    <Column fullWidth>
                        {ISSUE_TYPES.map((type) => (
                            <Column key={type} fullWidth>
                                <Option>
                                    <Radio value={type}
                                        isSelected={issueType === type}
                                        onChange={() => changeIssueType(type)}>
                                        {type === 'BUSINESS'
                                            ? t('cashReceipt.business')
                                            : t('cashReceipt.private')}
                                    </Radio>
                                </Option>
                                {issueType === type && (
                                    <TextField value={formattedNumber}
                                        placeholder={isBusiness
                                            ? t('cashReceipt.businessPlaceholder')
                                            : t('cashReceipt.contactPlaceholder')}
                                        inputMode='numeric'
                                        maxLength={isBusiness ? 12 : 13}
                                        errorText={error}
                                        onChange={(e) => changeIssueNumber(e.target.value)} />
                                )}
                            </Column>
                        ))}
                    </Column>
                    <NoticeBox>
                        <Text size='body-long' color={colors.textSecondary}>
                            {t('cashReceipt.notice')}
                        </Text>
                    </NoticeBox>
                    <SaveSwitch>
                        <Switch label={t('cashReceipt.saveNumber')}
                            checked={isSave}
                            setChecked={changeSave} />
                    </SaveSwitch>
                </>
            )}
        </Container>
    );
}

const Container = styled(Column)`
    align-items: flex-end;
    padding: var(--spacing-6) var(--spacing-4);
`;

const Header = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

const Segment = styled.div`
    background-color: var(--color-background-raised);
    border-radius: var(--radius-full);
    display: flex;
    flex-shrink: 0;
    padding: var(--spacing-1);
    width: 160px;
`;

const SegmentButton = styled.button`
    background-color: transparent;
    border-radius: var(--radius-full);
    color: var(--color-text-unseledcted);
    flex: 1;
    font-size: var(--font-size-body-short);
    font-weight: 500;
    height: 40px;
    min-width: 0;
    padding: 0;
    white-space: nowrap;

    &[aria-pressed="true"] {
        background-color: var(--color-button-default);
        color: var(--color-text-reverse);
        font-weight: 700;
    }
`;

const Option = styled.div`
    padding: var(--spacing-2) 0;
    width: 100%;
`;

const NoticeBox = styled.div`
    background-color: var(--color-background-raised);
    border-radius: var(--radius-sm);
    padding: var(--spacing-4);
    width: 100%;
`;

const SaveSwitch = styled.div`
    width: 180px;
`;

export default CashReceipt;
